/**
 * PII-redactie voor client-side Application Insights-telemetrie.
 *
 * De klant-onboarding-token zit in een URL-PAD-segment
 * (`/onboarding/relatie/<token>`, `/api/v1/client-onboarding/token/<token>/...`),
 * niet in de query. Dat mag NOOIT naar telemetrie lekken: het ondergraaft de
 * bewuste `Referrer-Policy: no-referrer`-hardening op de klant-app en CLAUDE.md
 * verbiedt het loggen van tokens. De SDK zet URL's/paden op meerdere plekken in
 * een telemetrie-item (page view `uri`, dependency `target`/`name`, de
 * operatienaam in `ext.trace.name` -> wire-tag `ai.operation.name`, en
 * exception-messages); deze module redigeert ze allemaal op één plek.
 */

import { decodeReactError } from './react-error-codes'

/**
 * Vervangt alleen de TOKEN-dragende segmenten door placeholders (geen cuids),
 * zonder de string af te kappen. Voor telemetrie-properties die bewust
 * herleidbaar blijven op een opake id (bv. het documentId in een proxy-pad,
 * zie `telemetry-fout.ts`) maar nooit een credential mogen dragen.
 */
export function redactTokenSegments(text: string): string {
  return text
    .replace(/(\/onboarding\/relatie\/)[^/\s?#]+/gi, '$1[token]')
    .replace(/(\/client-onboarding\/token\/)[^/\s?#]+/gi, '$1[token]')
    .replace(/(\/welkom\/)[^/\s?#]+/gi, '$1[token]')
    // Generieke bearer-tokens (bv. het HMAC-welkom-token `<b64url>.<b64url>`, of
    // JWT-achtig): twee of meer lange base64url-delen met punt(en) - vrijwel
    // altijd een credential, nooit een legitiem pad-segment of bestandsnaam.
    .replace(/[A-Za-z0-9_-]{20,}(?:\.[A-Za-z0-9_-]{20,})+/g, '[token]')
}

/**
 * Vervangt token-/id-dragende pad-segmenten door placeholders, zonder de string
 * af te kappen (veilig voor vrije tekst zoals exception-messages).
 */
export function redactSensitiveSegments(text: string): string {
  return redactTokenSegments(text).replace(/\bc[a-z0-9]{24}\b/g, '[id]')
}

/** Stript query/hash van een URL en redigeert daarna token-/id-pad-segmenten. */
export function redactUrl(value: string): string {
  const cut = value.search(/[?#]/)
  const path = cut === -1 ? value : value.slice(0, cut)
  return redactSensitiveSegments(path)
}

/** Minimale vorm van een App-Insights-envelope die we in-place redigeren. */
export interface RedactableTelemetryItem {
  ext?: Record<string, unknown>
  baseData?: Record<string, unknown>
}

/**
 * Redigeert in-place alle plekken waar de SDK een URL/pad (en dus mogelijk het
 * onboarding-token of een cuid) neerzet:
 * - `ext.trace.name` - de operatienaam (= `location.pathname`); de channel
 *   promoveert die ná de telemetry-initializer naar de wire-tag
 *   `ai.operation.name`, dus dít is het load-bearing veld (niet de tag, die is
 *   tijdens de initializer nog leeg).
 * - `baseData.uri/refUri/url/target/name` - page view- + dependency-URL's.
 * - `baseData.exceptions[].message` - kan een token-dragende URL bevatten; hier
 *   decoderen we bovendien "Minified React error #NNN" naar de leesbare melding
 *   (zie `react-error-codes.ts`).
 */
export function redactTelemetryItem(item: RedactableTelemetryItem): void {
  const trace = item.ext?.['trace'] as { name?: unknown } | undefined
  if (trace && typeof trace.name === 'string') {
    trace.name = redactUrl(trace.name)
  }

  const base = item.baseData
  if (!base) return

  // `data` MOET erbij: de App Insights JS-SDK zet de volledige request-URL van
  // een AJAX-dependency in `baseData.data` (en `name`); `target` is enkel de host.
  // Zonder `data` lekt een token-dragende pad-URL alsnog naar AppDependencies.
  for (const key of ['uri', 'refUri', 'url', 'target', 'name', 'data'] as const) {
    const v = base[key]
    if (typeof v === 'string') base[key] = redactUrl(v)
  }

  const exceptions = base['exceptions']
  if (Array.isArray(exceptions)) {
    for (const ex of exceptions) {
      const msg = (ex as { message?: unknown } | null)?.message
      if (typeof msg === 'string') {
        // Eerst PII redigeren, dán de "Minified React error #NNN"-code decoderen;
        // de leesbare tekst die we toevoegen is een vaste, token-vrije string.
        ;(ex as { message: string }).message = decodeReactError(redactSensitiveSegments(msg))
      }
    }
  }
}
